import { vec2 } from "gl-matrix";

/**
 * @typedef {"bottom" | "top" | "right" | "left"} GripSide
 * @typedef {{ left: number, top: number, right: number, bottom: number }} Rect
 */

/**
 * Sides the rotate grip may sit on, in the order they're tried. In tool
 * space, so they turn with the element.
 * @type {GripSide[]}
 */
const GRIP_SIDES = ["bottom", "top", "right", "left"];

/** Unit vector out of the tool from each side, in tool space. */
const OUTWARD = { bottom: [0, 1], top: [0, -1], right: [1, 0], left: [-1, 0] };

/**
 * The rotate grip's stalk on a side of the tool: from the middle of that edge
 * straight out by stalkLength. Plain arrays, so the points keep full precision.
 * @param {GripSide} side
 * @param {number} width - Tool width, in tool px.
 * @param {number} height - Tool height, in tool px.
 * @param {number} stalkLength - In tool px.
 * @returns {{ base: number[], grip: number[] }} Points in tool space.
 */
export const getGripStalk = (side, width, height, stalkLength) => {
  const half = [width / 2, height / 2];
  // the edge's middle: out from the center by half the size
  const base = vec2.add([0, 0], half, vec2.multiply([0, 0], OUTWARD[side], half));
  return { base, grip: vec2.scaleAndAdd([0, 0], base, OUTWARD[side], stalkLength) };
};

/**
 * Whether a circle lies wholly inside a rect.
 * @param {number[]} center
 * @param {number} radius
 * @param {Rect} rect
 * @returns {boolean}
 */
const circleInRect = ([x, y], radius, rect) =>
  x - radius >= rect.left &&
  x + radius <= rect.right &&
  y - radius >= rect.top &&
  y + radius <= rect.bottom;

/**
 * The first side whose grip, with its clearance, is wholly on screen; the
 * bottom when none is (an element bigger than the view).
 * @param {Object} props
 * @param {number} props.width - Tool width, in tool px.
 * @param {number} props.height - Tool height, in tool px.
 * @param {number} props.stalkLength - In tool px.
 * @param {number} props.clearance - Screen px the grip needs around its center.
 * @param {number[]} props.matrix - Tool space → design px of the tool's container (the drawn tool's matrix).
 * @param {number[]} props.origin - Client px of the container's top-left.
 * @param {number} props.zoom - Design px → screen px.
 * @param {Rect} props.visibleRect - Client px the grip must stay inside.
 * @returns {GripSide}
 */
export const chooseGripSide = ({
  width,
  height,
  stalkLength,
  clearance,
  matrix,
  origin,
  zoom,
  visibleRect,
}) =>
  GRIP_SIDES.find((side) => {
    const { grip } = getGripStalk(side, width, height, stalkLength);
    const design = vec2.transformMat2d([0, 0], grip, matrix);
    const screen = vec2.scaleAndAdd([0, 0], origin, design, zoom);
    return circleInRect(screen, clearance, visibleRect);
  }) ?? "bottom";
